// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCsrfToken, fetchUser } from '../utils/auth.js';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';         
import { Link } from 'react-router-dom';
import { Check, X, ChevronDown } from 'lucide-react';
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLegend,
} from "../components/ui/field";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // ── States
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [userType, setUserType] = useState(null);
  const [showUserTypeSelection, setShowUserTypeSelection] = useState(true);
  const [showPassword, setShowPassword] = useState(false);   // login
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [showReferralOptions, setShowReferralOptions] = useState(false);
  const [authContextMessage, setAuthContextMessage] = useState(null);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    notNumeric: false,
    notSimilar: false,
    match: false,
  });
  const [isFormValid, setIsFormValid] = useState(false);

  // Global user query (replaces the one in Home)
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user'],
    queryFn: fetchUser,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Validation effect (exactly as you had)
  useEffect(() => {
    const { username = '', password1 = '', password2 = '', location = '' } = formData;
    const newErrors = {};

    if (username.length < 3 || !/^[a-zA-Z0-9._-]+$/.test(username)) {
      newErrors.username = 'At least 3 characters, letters/numbers/._- only';
    }
    if (!location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));

    const pwReqs = {
      length: password1.length >= 8,
      notNumeric: password1.length > 0 && !/^\d+$/.test(password1),
      notSimilar:
        password1.length > 0 &&
        !password1.toLowerCase().includes(username.toLowerCase()) &&
        !password1.toLowerCase().includes((formData.email || '').split('@')[0].toLowerCase()),
      match: password1 === password2 && password2 !== '',
    };
    setPasswordRequirements(pwReqs);

    const allPwValid = Object.values(pwReqs).every(Boolean);
    const noRelevantErrors = !newErrors.username && !newErrors.location;
    setIsFormValid(noRelevantErrors && allPwValid);
  }, [formData]);

  // Handlers (copied & adapted)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
    setErrors((prev) => ({ ...prev, terms: undefined }));
  };

  const closeModal = () => {
    setShowLogin(false);
    setShowRegister(false);
    setFormData({});
    setErrors({});
    setTermsAccepted(false);
    setUserType(null);
    setShowUserTypeSelection(true);
    setShowReferralOptions(false);
    setAuthContextMessage(null);
  };

  const openLogin = (contextMessage = null) => {
    closeModal();
    setAuthContextMessage(contextMessage);
    setShowLogin(true);
  };

  const openRegister = (contextMessage = null) => {
    closeModal();
    setAuthContextMessage(contextMessage);
    setShowRegister(true);
  };

  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      await axios.post(`${process.env.REACT_APP_API_URL}/api/logout/`, {}, { withCredentials: true });
      queryClient.removeQueries(['user']);
      queryClient.setQueryData(['user'], null);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!userType) {
      setErrors({ all: ['Please select a user type (Regular User or Service Provider).'] });
      setShowUserTypeSelection(true);
      return;
    }

    if (userType === 'provider') {
      if (!formData.service_provided) {
        setErrors({ service_provided: ['Service offered is required for providers.'] });
        return;
      }
      if (!formData.business_name) {
        setErrors({ business_name: ['Business name is required for providers.'] });
        return;
      }
    }

    if (!termsAccepted) {
      setErrors({ terms: ['You must agree to the Terms of Service and Privacy Policy.'] });
      return;
    }

    setIsAuthLoading(true);

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        data.append(key, value);
      }
    });
    data.append('user_type', userType);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/register/`, data, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Close modals and reset everything
      closeModal();
      setShowLogin(true);           // Switch to login after successful register

      setTermsAccepted(false);
      setFormData({});
      setErrors({});
      setShowUserTypeSelection(true);

    } catch (error) {
      const errorData = error.response?.data?.errors || {
        all: ['Registration failed—please check your inputs or try again later.'],
      };
      setErrors(errorData);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsAuthLoading(true);

    try {
      const csrfToken = await getCsrfToken();
      await axios.post(`${process.env.REACT_APP_API_URL}/api/login/`, formData, {
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      queryClient.invalidateQueries(['user']);
      closeModal();

      // NEW: Check for pending booking and restore it (slug-aware)
      const pendingData = localStorage.getItem('pendingBooking');
      if (pendingData) {
        try {
          const booking = JSON.parse(pendingData);

          toast.success("Login successful! Restoring your order...", {
            description: "Please review and confirm your booking.",
            duration: 6000,
          });

          // Prefer slug if available, otherwise fallback to ID
          const targetUrl = booking.providerSlug 
            ? `/provider/${booking.providerSlug}` 
            : `/provider/${booking.providerId}`;

          navigate(targetUrl, {
            state: { 
              restoreBooking: booking 
            }
          });

          localStorage.removeItem('pendingBooking');
        } catch (e) {
          console.error("Failed to restore pending booking:", e);
          localStorage.removeItem('pendingBooking');
        }
      }
    } catch (error) {
      console.error('[Auth] Login Error:', error.response?.status);
      setErrors(error.response?.data?.errors || { all: ['Login failed'] });
    } finally {
      setIsAuthLoading(false);
    }
  };

  const getReferralIcon = (value) => {
    const map = {
      friend: 'fas fa-user-friends',
      admin_intro: 'fas fa-handshake',
      facebook: 'fab fa-facebook-f',
      instagram: 'fab fa-instagram',
      whatsapp: 'fab fa-whatsapp',
      tiktok: 'fab fa-tiktok',
      twitter: 'fab fa-x-twitter',
      google: 'fab fa-google',
      ai: 'fas fa-robot',
      poster: 'fas fa-bullhorn',
      radio_tv: 'fas fa-radio',
      other: 'fas fa-question-circle',
    };
    return map[value] || 'fas fa-question-circle';
  };

  const getReferralLabel = (value) => {
    const map = {
      friend: 'Friend or Family',
      admin_intro: 'Admin Introduced Me',
      facebook: 'Facebook',
      instagram: 'Instagram',
      whatsapp: 'WhatsApp',
      tiktok: 'TikTok',
      twitter: 'X / Twitter',
      google: 'Google Search',
      ai: 'AI (Grok, ChatGPT, etc.)',
      poster: 'Poster',
      radio_tv: 'Radio / TV',
      other: 'Other',
    };
    return map[value] || 'Tap to select';
  };

  const value = {
    user,
    userLoading,
    isAuthLoading,
    handleLogout,
    openLogin,
    openRegister,
    authContextMessage,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {showLogin && (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-border">
      <div className="p-6 md:p-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex-1">
            <h2 className="text-3xl font-bold text-foreground">
              {authContextMessage?.title || "Welcome Back! 👋"}
            </h2>
            {authContextMessage?.subtitle && (
              <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2x1">
                <div className="flex items-start gap-3">
                  <span className="text-2xl mt-0.5">📌</span>
                  <p className="text-primary font-medium text-[17px] leading-relaxed">
                    {authContextMessage.subtitle}
                  </p>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={closeModal}
            className="text-muted-foreground hover:text-foreground text-2xl transition-colors"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <FieldGroup>
            <Field orientation="vertical">
              <FieldLabel htmlFor="username_or_email">Username or Email</FieldLabel>
              <FieldContent>
                <Input
                  id="username_or_email"
                  name="username_or_email"
                  onChange={handleInputChange}
                  placeholder="Enter username or email"
                  className="text-base"
                  required
                />
              </FieldContent>
            </Field>

            <Field orientation="vertical">
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <FieldContent>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    className="pr-12 text-base"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </FieldContent>
            </Field>
          </FieldGroup>

          {errors.all && Array.isArray(errors.all) && (
            <FieldError>{errors.all[0]}</FieldError>
          )}

          <div className="flex justify-between items-center text-sm">
            <Link
              to="/forgot-password"
              className="text-primary hover:text-primary/80 transition-colors underline underline-offset-4"
            >
              Forgot Password?
            </Link>
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={isAuthLoading}
              className="flex-1"
              size="lg"
            >
              {isAuthLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Logging in...
                </div>
              ) : (
                'Login'
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={closeModal}
              size="lg"
            >
              Cancel
            </Button>
          </div>

          <div className="text-center pt-4 border-t">
            <p className="text-muted-foreground text-sm">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  setShowLogin(false);
                  setShowRegister(true);
                }}
                className="text-primary hover:text-primary/80 font-medium transition-colors underline underline-offset-4"
              >
                Register here
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  </div>
)}

      {/* Register Modal */}
{showRegister && (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-border">
      <div className="p-6 md:p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-foreground">
            Join SErraND Squad! 🚀
          </h2>
          <button
            onClick={closeModal}
            className="text-muted-foreground hover:text-foreground text-2xl transition-colors"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {showUserTypeSelection ? (
  <div className="space-y-6">
    <p className="text-muted-foreground text-base text-center">
      Are you looking for services or offering them?
    </p>

    <div className="grid grid-cols-1 gap-4">
      <Button
        variant="default"
        className="h-auto py-4 px-5 text-left"
        onClick={() => {
          setUserType('user');
          setShowUserTypeSelection(false);
        }}
      >
        <div className="space-y-1">
          <div className="font-semibold text-white dark:text-white text-left">Join as Customer</div>
          <div className="text-xs text-white dark:text-white text-left text-muted-foreground leading-tight">
            Looking for help with errands or tasks
          </div>
        </div>
      </Button>

      <Button
        variant="outline"
        className="h-auto py-4 px-5 text-left"
        onClick={() => {
          setUserType('provider');
          setShowUserTypeSelection(false);
        }}
      >
        <div className="space-y-1">
          <div className="font-semibold text-base">Join as Service Provider</div>
          <div className="text-xs text-muted-foreground leading-tight">
            Offering services like plumbing, cleaning, deliveries...
          </div>
        </div>
      </Button>
    </div>

    <div className="text-center pt-4">
      <p className="text-muted-foreground text-sm">
        Already have an account?{' '}
        <button
          type="button"
          onClick={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
          className="text-primary hover:text-primary/80 font-medium transition-colors underline underline-offset-4"
        >
          Login here
        </button>
      </p>
    </div>
  </div>
) : (
          <form onSubmit={handleRegister} className="space-y-6">
            <div className="text-sm text-muted-foreground mb-4">
              Registering as:{' '}
              <span className="font-semibold text-foreground">
                {userType === 'user' ? 'Regular User' : 'Service Provider'}
              </span>{' '}
              <button
                type="button"
                onClick={() => setShowUserTypeSelection(true)}
                className="text-primary hover:text-primary/80 underline text-sm ml-1"
              >
                Change
              </button>
            </div>

            {/* Core fields */}
            <FieldGroup>
              <Field orientation="vertical">
                <FieldLabel htmlFor="username">Username (min 3 chars)</FieldLabel>
                <Input
                  id="username"
                  name="username"
                  value={formData.username || ''}
                  onChange={handleInputChange}
                  placeholder="Choose a username"
                  required
                />
                {errors.username && <FieldError>{errors.username}</FieldError>}
              </Field>

              <Field orientation="vertical">
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  required
                />
              </Field>

              <Field orientation="vertical">
                <FieldLabel htmlFor="password1">Password</FieldLabel>
                <div className="relative">
                  <Input
                    id="password1"
                    name="password1"
                    type={showPassword1 ? 'text' : 'password'}
                    value={formData.password1 || ''}
                    onChange={handleInputChange}
                    placeholder="Create a strong password"
                    className="pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword1(!showPassword1)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword1 ? 'Hide password' : 'Show password'}
                  >
                    {showPassword1 ? '🙈' : '👁️'}
                  </button>
                </div>
              </Field>

              <Field orientation="vertical">
                <FieldLabel htmlFor="password2">Confirm Password</FieldLabel>
                <div className="relative">
                  <Input
                    id="password2"
                    name="password2"
                    type={showPassword2 ? 'text' : 'password'}
                    value={formData.password2 || ''}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                    className="pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword2(!showPassword2)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword2 ? 'Hide password' : 'Show password'}
                  >
                    {showPassword2 ? '🙈' : '👁️'}
                  </button>
                </div>
              </Field>

              <FieldDescription>
                <ul className="text-sm space-y-1 mt-2">
                  <li className="flex items-center gap-2">
                    {passwordRequirements.length ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />}
                    At least 8 characters
                  </li>
                  <li className="flex items-center gap-2">
                    {passwordRequirements.notNumeric ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />}
                    Not entirely numbers
                  </li>
                  <li className="flex items-center gap-2">
                    {passwordRequirements.notSimilar ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />}
                    Not too similar to username or email
                  </li>
                  <li className="flex items-center gap-2">
                    {passwordRequirements.match ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />}
                    Passwords match
                  </li>
                </ul>
              </FieldDescription>

              <Field orientation="vertical">
                <FieldLabel htmlFor="location">Location (e.g., Yukos Kitengela)</FieldLabel>
                <Input
                  id="location"
                  name="location"
                  value={formData.location || ''}
                  onChange={handleInputChange}
                  placeholder="Your location"
                  required
                />
                {errors.location && <FieldError>{errors.location}</FieldError>}
              </Field>
            </FieldGroup>

            {/* WhatsApp Number */}
            <Field orientation="vertical">
              <FieldLabel>WhatsApp Number (for order updates – recommended)</FieldLabel>
              <div className="flex border border-input rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-ring">
                <div className="bg-muted px-4 py-3 text-muted-foreground font-medium border-r border-input flex items-center gap-2">
                  <i className="fab fa-whatsapp text-green-500" />
                  <span>+254</span>
                </div>
                <Input
                  name="whatsapp_notification_number"
                  type="tel"
                  placeholder="712345678"
                  value={(formData.whatsapp_notification_number || '').replace('+254', '')}
                  onChange={(e) => {
                    let num = e.target.value.replace(/\D/g, '').slice(0, 9);
                    handleInputChange({
                      target: {
                        name: 'whatsapp_notification_number',
                        value: num ? '+254' + num : ''
                      }
                    });
                  }}
                  className="flex-1 border-none focus:ring-0"
                  required={false}
                />
              </div>
            </Field>

            {/* Referral source */}
            <Field orientation="vertical">
              <FieldLabel>How did you hear about SErraND?</FieldLabel>
              <button
                type="button"
                onClick={() => setShowReferralOptions(!showReferralOptions)}
                className="w-full px-4 py-3 bg-muted border border-input rounded-lg text-left flex items-center justify-between hover:bg-accent transition-all text-sm"
              >
                <span className="flex items-center gap-3">
                  {formData.how_did_you_hear ? (
                    <>
                      <i
                        className={`fa-fw text-lg ${
                          ['facebook','instagram','whatsapp','tiktok','twitter','google'].includes(formData.how_did_you_hear)
                            ? 'fab'
                            : 'fas'
                        } fa-${getReferralIcon(formData.how_did_you_hear)} ${
                          formData.how_did_you_hear ? 'text-primary' : 'text-muted-foreground'
                        }`}
                      />
                      <span>{getReferralLabel(formData.how_did_you_hear)}</span>
                      {formData.how_did_you_hear === 'other' && formData.how_did_you_hear_other && (
                        <span className="text-primary text-xs">({formData.how_did_you_hear_other})</span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">Tap to select...</span>
                  )}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showReferralOptions ? 'rotate-180' : ''}`} />
              </button>

              {showReferralOptions && (
                <div className="mt-2 border border-input rounded-lg p-3 bg-muted/50 max-h-64 overflow-y-auto grid grid-cols-1 gap-2">
                  {[
                    { value: 'friend', icon: 'user-friends', label: 'Friend or Family', type: 'fas' },
                    { value: 'admin_intro', icon: 'handshake', label: 'Admin Introduced Me', type: 'fas' },
                    { value: 'facebook', icon: 'facebook-f', label: 'Facebook', type: 'fab' },
                    { value: 'instagram', icon: 'instagram', label: 'Instagram', type: 'fab' },
                    { value: 'whatsapp', icon: 'whatsapp', label: 'WhatsApp', type: 'fab' },
                    { value: 'tiktok', icon: 'tiktok', label: 'TikTok', type: 'fab' },
                    { value: 'twitter', icon: 'x-twitter', label: 'X / Twitter', type: 'fab' },
                    { value: 'google', icon: 'google', label: 'Google Search', type: 'fab' },
                    { value: 'ai', icon: 'robot', label: 'AI (Grok, ChatGPT, etc.)', type: 'fas' },
                    { value: 'poster', icon: 'bullhorn', label: 'Poster', type: 'fas' },
                    { value: 'radio_tv', icon: 'radio', label: 'Radio / TV', type: 'fas' },
                    { value: 'other', icon: 'circle-question', label: 'Other', type: 'fas' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          how_did_you_hear: opt.value,
                          how_did_you_hear_other: opt.value === 'other' ? prev.how_did_you_hear_other : ''
                        }));
                        setShowReferralOptions(false);
                      }}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-all text-left w-full"
                    >
                      <i className={`fa-fw text-lg ${opt.type} fa-${opt.icon} text-muted-foreground`} />
                      <span className="text-sm">{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {formData.how_did_you_hear === 'other' && (
                <Input
                  name="how_did_you_hear_other"
                  placeholder="e.g. YouTube, Church, School..."
                  value={formData.how_did_you_hear_other || ''}
                  onChange={handleInputChange}
                  className="mt-3 text-sm"
                />
              )}
            </Field>

            {/* Provider-specific fields */}
            {userType === 'provider' && (
              <FieldGroup className="p-6 bg-muted/50 rounded-xl border border-border space-y-6">
                <FieldLegend variant="legend">Provider Details</FieldLegend>

                <Field orientation="vertical">
                  <FieldLabel htmlFor="business_name">Brand Name</FieldLabel>
                  <Input
                    id="business_name"
                    name="business_name"
                    value={formData.business_name || ''}
                    onChange={handleInputChange}
                    placeholder="e.g. Blessed Nail Art Service"
                    required
                  />
                </Field>

                <Field orientation="vertical">
                  <FieldLabel htmlFor="service_provided">Service Provided</FieldLabel>
                  <Input
                    id="service_provided"
                    name="service_provided"
                    value={formData.service_provided || ''}
                    onChange={handleInputChange}
                    placeholder="Be clear and precise (e.g., House hunting Service)"
                    required
                  />
                  {errors.service_provided && <FieldError>{errors.service_provided[0]}</FieldError>}
                </Field>

                <Field orientation="vertical">
                  <FieldLabel htmlFor="description">Description</FieldLabel>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description || ''}
                    placeholder="Description - DO NOT input personal contact info"
                    className="w-full px-4 py-3 bg-background border border-input rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-ring min-h-[100px] resize-none"
                    onChange={handleInputChange}
                  />
                </Field>

                <Field orientation="vertical">
                  <FieldLabel htmlFor="business_hours">Work Hours</FieldLabel>
                  <Input
                    id="business_hours"
                    name="business_hours"
                    value={formData.business_hours || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., 24/7, Mon-Sat 8am-6pm"
                  />
                </Field>
              </FieldGroup>
            )}

            {/* Terms */}
            <Field orientation="horizontal">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onChange={handleTermsChange}
                  className="mt-1 h-4 w-4 text-primary border-input rounded focus:ring-primary"
                  required
                />
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed">
                  I agree to the{' '}
                  <Link to="/terms" className="text-primary hover:underline" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-primary hover:underline" target="_blank">
                    Privacy Policy
                  </Link>
                </label>
              </div>
              {errors.terms && <FieldError>{errors.terms}</FieldError>}
            </Field>

            {/* All other errors */}
            {errors.all && Array.isArray(errors.all) && (
              <FieldError>{errors.all[0]}</FieldError>
            )}
            {Object.entries(errors).map(([key, value]) => {
              if (key === 'all' || key === 'service_provided' || key === 'terms' || !Array.isArray(value)) return null;
              return <FieldError key={key}>{value[0]}</FieldError>;
            })}

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={isAuthLoading} className="flex-1" size="lg">
                {isAuthLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Registering...
                  </div>
                ) : (
                  'Register'
                )}
              </Button>

              <Button type="button" variant="outline" onClick={closeModal} size="lg">
                Cancel
              </Button>
            </div>

            <div className="text-center pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setShowRegister(false);
                    setShowLogin(true);
                  }}
                  className="text-primary hover:text-primary/80 font-medium transition-colors underline underline-offset-4"
                >
                  Login here
                </button>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  </div>
)}

    </AuthContext.Provider>
  );
}
